// StatBlock — number-forward stat card: overline label, large tabular value,
// optional sparkline and delta chip, and a required sub-label. The sub-label
// is mandatory because the sparkline is aria-hidden decoration — the adjacent
// text is the accessible representation of the trend.

import type { ReactNode } from "react";
import { clsx } from "clsx";
import Card from "./Card";
import Sparkline from "./Sparkline";

export type StatDeltaDirection = "up" | "down" | "flat";

interface StatBlockProps {
  label: string;
  value: ReactNode;
  sub: string;
  series?: number[];
  sparklineClassName?: string;
  delta?: { label: string; direction: StatDeltaDirection };
}

const deltaClasses: Record<StatDeltaDirection, string> = {
  up: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  down: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  flat: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

export default function StatBlock({
  label,
  value,
  sub,
  series,
  sparklineClassName,
  delta,
}: StatBlockProps) {
  return (
    <Card padding="md">
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-overline uppercase text-slate-500 dark:text-slate-400">
            {label}
          </p>
          {series && series.length > 0 && (
            <Sparkline data={series} className={sparklineClassName} />
          )}
        </div>
        <div className="text-display leading-none tabular-nums text-slate-900 dark:text-slate-50">
          {value}
        </div>
        <div className="flex items-center gap-2.5">
          {delta && (
            <span
              className={clsx(
                "inline-flex items-center rounded-full px-[7px] py-[2px] text-caption font-semibold",
                deltaClasses[delta.direction],
              )}
            >
              {delta.label}
            </span>
          )}
          <span className="text-caption text-slate-500 dark:text-slate-400">
            {sub}
          </span>
        </div>
      </div>
    </Card>
  );
}
